import { LogIn, Mail, Pencil, Plus, Trash2, UserCheck, UserX, Users, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useImpersonateUser } from "@/api/hooks";
import {
  useCurrentUser,
  useDeleteUser,
  useInviteUser,
  useOrganizations,
  useRequestMagicLink,
  useUpdateUser,
  useUsers,
} from "@/api/hooks";
import type { OrganizationSummary, UserResponse, UserRole } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatTime, shortId } from "@/lib/format";

export function UsersPage() {
  const me = useCurrentUser();
  const myRole = me.data?.role;
  const isPencilOutAdmin = myRole === "pencilout_admin";
  const canManageUsers = myRole === "pencilout_admin" || myRole === "org_admin";

  // Org filter — pencilout_admin only. Empty string means "all orgs".
  const [orgFilter, setOrgFilter] = useState("");
  const usersQuery = useUsers(isPencilOutAdmin && orgFilter ? orgFilter : null);
  // Only admins need the org list (for filter dropdown + invite-form
  // org picker). org_members would 403 on /organizations anyway.
  const orgsQuery = useOrganizations();

  const [inviting, setInviting] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-graphite-soft">
            {isPencilOutAdmin
              ? "All users across every organization. Invite teammates or PencilOut staff."
              : canManageUsers
                ? "Members of your organization. Invite teammates and manage their access."
                : "Members of your organization."}
          </p>
        </div>
        {canManageUsers && !inviting && (
          <button
            type="button"
            onClick={() => setInviting(true)}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Invite user
          </button>
        )}
      </div>

      {canManageUsers && inviting && (
        <InviteUserForm
          onClose={() => setInviting(false)}
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
        />
      )}

      {isPencilOutAdmin && orgsQuery.data && orgsQuery.data.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="label-mono">Filter by org:</span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="rounded border border-rule bg-paper-warm px-2 py-1 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            <option value="">All organizations + staff</option>
            {orgsQuery.data.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {usersQuery.isLoading && <p className="text-sm text-graphite-soft">Loading users…</p>}

      {/* Show a hard error only when there's no cached data to render.
          A failed background refetch shouldn't override a working list. */}
      {usersQuery.error && !usersQuery.data && (
        <p className="text-sm text-red-pencil">
          {usersQuery.error instanceof ApiError ? usersQuery.error.detail : "Could not load users."}
        </p>
      )}

      {usersQuery.data && usersQuery.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Users className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">No users yet. Invite one above to get started.</p>
        </div>
      )}

      {usersQuery.data && usersQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
          <table className="w-full">
            <thead className="border-b border-rule bg-paper">
              <tr>
                <th className="label-mono px-4 py-2 text-left">Name</th>
                <th className="label-mono px-4 py-2 text-left">Email</th>
                <th className="label-mono px-4 py-2 text-left">Role</th>
                {isPencilOutAdmin && (
                  <th className="label-mono px-4 py-2 text-left">Organization</th>
                )}
                <th className="label-mono px-4 py-2 text-left">Status</th>
                <th className="label-mono px-4 py-2 text-right">Last login</th>
                {canManageUsers ? (
                  <th className="label-mono px-4 py-2 text-right">Actions</th>
                ) : (
                  <th className="label-mono px-4 py-2 text-right">ID</th>
                )}
              </tr>
            </thead>
            <tbody>
              {usersQuery.data.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isPencilOutAdmin={isPencilOutAdmin}
                  canManageUsers={canManageUsers}
                  isSelf={user.id === me.data?.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  isPencilOutAdmin,
  canManageUsers,
  isSelf,
}: {
  user: UserResponse;
  isPencilOutAdmin: boolean;
  /** When false, render the row read-only (no resend / no disable). */
  canManageUsers: boolean;
  isSelf: boolean;
}) {
  const updateUser = useUpdateUser(user.id);
  const deleteUser = useDeleteUser();
  const requestLink = useRequestMagicLink();
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [resendNotice, setResendNotice] = useState<"idle" | "sent" | "error">("idle");

  const isDisabled = user.status === "disabled";
  const isPending = updateUser.isPending || requestLink.isPending || deleteUser.isPending;

  function onConfirmToggleStatus() {
    updateUser.mutate(
      { status: isDisabled ? "active" : "disabled" },
      { onSettled: () => setConfirmingDisable(false) },
    );
  }

  function onConfirmDelete() {
    deleteUser.mutate(user.id, {
      onSettled: () => setConfirmingDelete(false),
    });
  }

  async function onResendInvite() {
    setResendNotice("idle");
    if (!user.email) {
      // Phone-only invitee — no magic-link to resend. They sign in
      // via SMS OTP themselves.
      setResendNotice("error");
      setTimeout(() => setResendNotice("idle"), 4000);
      return;
    }
    try {
      await requestLink.mutateAsync({ email: user.email });
      setResendNotice("sent");
      setTimeout(() => setResendNotice("idle"), 3000);
    } catch {
      setResendNotice("error");
      setTimeout(() => setResendNotice("idle"), 4000);
    }
  }

  return (
    <tr className="border-b border-rule last:border-0 hover:bg-pencil-yellow-soft">
      <td className="px-4 py-3 font-medium text-graphite">
        {user.display_name}
        {isSelf && (
          <span className="ml-2 rounded bg-paper px-1.5 py-0.5 font-mono text-[10px] text-graphite-soft">
            you
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-graphite-soft">{user.email}</td>
      <td className="px-4 py-3 font-mono text-xs text-graphite">{user.role}</td>
      {isPencilOutAdmin && (
        <td className="px-4 py-3 text-graphite-soft">
          {user.organization_name || <span className="text-graphite-faint">— (staff)</span>}
        </td>
      )}
      <td className="px-4 py-3">
        <span
          className={[
            "rounded px-2 py-0.5 font-mono text-xs",
            user.status === "active"
              ? "bg-pencil-yellow-soft text-graphite"
              : user.status === "invited"
                ? "bg-paper text-graphite-soft"
                : "bg-red-pencil/10 text-red-pencil",
          ].join(" ")}
        >
          {user.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs text-graphite-soft tabular-nums">
        {user.last_login_at ? formatTime(user.last_login_at) : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        {!canManageUsers ? (
          <span className="font-mono text-xs text-graphite-faint">{shortId(user.id)}</span>
        ) : (
          <>
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={onResendInvite}
                disabled={isPending || isDisabled}
                title={
                  isDisabled
                    ? "Re-enable before resending invite"
                    : user.status === "invited"
                      ? "Resend invite email"
                      : "Email a fresh sign-in link"
                }
                className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
              >
                <Mail className="h-3.5 w-3.5" aria-hidden />
              </button>
              {/* Edit identity — pencilout_admin escape hatch for
                  fixing typos / address changes. Self-service path
                  (with verification) lives in the user's own profile
                  page. Allowed even on yourself and on other admins
                  (typos happen) but disabled when the row is itself
                  disabled to keep the action set tight. */}
              {isPencilOutAdmin && !isDisabled && <EditIdentityButton user={user} />}
              {/* Sign in as — pencilout_admin only, not on yourself,
                  not on disabled accounts, not on other admins. */}
              {isPencilOutAdmin && !isSelf && !isDisabled && user.role !== "pencilout_admin" && (
                <ImpersonateButton userId={user.id} displayName={user.display_name} />
              )}
              {!isSelf && (
                <button
                  type="button"
                  onClick={() => setConfirmingDisable(true)}
                  disabled={isPending}
                  title={isDisabled ? "Re-enable user" : "Disable user"}
                  className={[
                    "rounded p-1.5 transition disabled:opacity-50",
                    isDisabled
                      ? "text-graphite-soft hover:bg-paper hover:text-graphite"
                      : "text-graphite-soft hover:bg-red-pencil/10 hover:text-red-pencil",
                  ].join(" ")}
                >
                  {isDisabled ? (
                    <UserCheck className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <UserX className="h-3.5 w-3.5" aria-hidden />
                  )}
                </button>
              )}
              {/* Hard delete — pencilout_admin only, never on yourself.
                  Disable is the everyday "remove access" pattern;
                  delete is for test signups, duplicate accounts, GDPR
                  erasure. The server 409s if the user authored jobs /
                  templates / webhooks (RESTRICT FKs) so the admin
                  can clean those up first. */}
              {isPencilOutAdmin && !isSelf && (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={isPending}
                  title="Delete user (permanent)"
                  className="rounded p-1.5 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
            <div className="mt-1 h-3 text-right">
              {resendNotice === "sent" && (
                <span className="font-mono text-[10px] text-pencil-yellow">sent</span>
              )}
              {resendNotice === "error" && (
                <span className="font-mono text-[10px] text-red-pencil">failed</span>
              )}
              {resendNotice === "idle" && (
                <span className="font-mono text-[10px] text-graphite-faint">
                  {shortId(user.id)}
                </span>
              )}
            </div>
          </>
        )}

        {confirmingDisable && (
          <ConfirmDialog
            title={isDisabled ? `Re-enable ${user.display_name}?` : `Disable ${user.display_name}?`}
            body={
              isDisabled ? (
                <>
                  This user will be able to sign in again on their next magic-link request. Existing
                  sessions are unaffected.
                </>
              ) : (
                <>
                  Disabling immediately signs out{" "}
                  <span className="font-mono text-graphite">{user.email}</span> on every device and
                  prevents new sign-ins. Re-enable any time — the user just needs a fresh magic
                  link.
                  {updateUser.error && (
                    <span className="mt-2 block text-red-pencil">
                      {updateUser.error instanceof ApiError
                        ? updateUser.error.detail
                        : "Could not update user."}
                    </span>
                  )}
                </>
              )
            }
            confirmLabel={isDisabled ? "Re-enable" : "Disable user"}
            variant={isDisabled ? "default" : "danger"}
            busy={updateUser.isPending}
            onConfirm={onConfirmToggleStatus}
            onCancel={() => {
              setConfirmingDisable(false);
              updateUser.reset();
            }}
          />
        )}

        {confirmingDelete && (
          <ConfirmDialog
            title={`Delete ${user.display_name}?`}
            body={
              <>
                <p>
                  Permanently removes{" "}
                  <span className="font-mono text-graphite">
                    {user.email ?? user.phone_number ?? user.display_name}
                  </span>
                  . Sessions, magic-link tokens, device tokens, and push preferences go away with
                  the user. Quote comments and audit-log entries authored by them stay (with author
                  set to null).
                </p>
                <p className="mt-2 text-graphite-soft">
                  If they authored jobs, templates, or webhooks, the delete will refuse with a 409 —
                  handle those first, or delete the whole organization to cascade-clean everything.
                </p>
                <p className="mt-2">
                  For "block sign-in but keep history," use{" "}
                  <span className="font-semibold text-graphite">Disable</span> instead. This is{" "}
                  <span className="font-semibold text-graphite">irreversible</span>.
                </p>
                {deleteUser.error && (
                  <p className="mt-2 text-red-pencil">
                    {deleteUser.error instanceof ApiError
                      ? deleteUser.error.detail
                      : "Could not delete user."}
                  </p>
                )}
              </>
            }
            confirmLabel="Delete user"
            variant="danger"
            busy={deleteUser.isPending}
            onConfirm={onConfirmDelete}
            onCancel={() => {
              setConfirmingDelete(false);
              deleteUser.reset();
            }}
          />
        )}
      </td>
    </tr>
  );
}

function InviteUserForm({
  onClose,
  isPencilOutAdmin,
  orgs,
}: {
  onClose: () => void;
  isPencilOutAdmin: boolean;
  orgs: OrganizationSummary[];
}) {
  const invite = useInviteUser();
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<UserRole>(isPencilOutAdmin ? "org_member" : "org_member");
  const [orgId, setOrgId] = useState("");

  // For pencilout_admin, the org picker only matters when role isn't
  // pencilout_admin. For org_admin, org is implicit; backend ignores
  // any organization_id we send.
  const needsOrg = isPencilOutAdmin && role !== "pencilout_admin";

  // Phone-only invites can't be made for pencilout_admin staff —
  // staff sign-in is email-only by design (we don't OTP staff).
  const phonePathAllowed = role !== "pencilout_admin";

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!displayName.trim()) return;
    if (method === "email" && !email.trim()) return;
    if (method === "phone" && phoneNumber.replace(/\D/g, "").length < 10) {
      return;
    }
    if (needsOrg && !orgId) return;
    try {
      await invite.mutateAsync({
        email: method === "email" ? email.trim() : undefined,
        phone_number: method === "phone" ? phoneNumber.trim() : undefined,
        display_name: displayName.trim(),
        role,
        organization_id: needsOrg ? orgId : null,
      });
      onClose();
    } catch {
      /* error surfaces via invite.error below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">Invite user</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-graphite-soft">
        {method === "phone" ? (
          <>
            We'll create the account in <span className="font-mono">invited</span> status with the
            phone number you enter. The invitee signs in by opening PencilOut and entering their
            phone — they'll get a 6-digit SMS code that transitions them to{" "}
            <span className="font-mono">active</span>. Tell them out-of-band that they've been
            added.
          </>
        ) : (
          <>
            We'll create the account in <span className="font-mono">invited</span> status and email
            a magic-link sign-in. Clicking the link transitions them to{" "}
            <span className="font-mono">active</span>.
          </>
        )}
      </p>

      {phonePathAllowed && (
        <div className="flex gap-1 rounded-md border border-rule bg-paper p-1">
          <button
            type="button"
            onClick={() => setMethod("email")}
            className={[
              "flex-1 rounded px-3 py-1 font-mono text-xs transition",
              method === "email"
                ? "bg-graphite text-paper"
                : "text-graphite-soft hover:text-graphite",
            ].join(" ")}
          >
            Email invite
          </button>
          <button
            type="button"
            onClick={() => setMethod("phone")}
            className={[
              "flex-1 rounded px-3 py-1 font-mono text-xs transition",
              method === "phone"
                ? "bg-graphite text-paper"
                : "text-graphite-soft hover:text-graphite",
            ].join(" ")}
          >
            Phone invite
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {method === "email" ? (
          <label className="block">
            <span className="label-mono">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
            />
          </label>
        ) : (
          <label className="block">
            <span className="label-mono">Phone</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(704) 555-1234"
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
            />
          </label>
        )}

        <label className="block">
          <span className="label-mono">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane Smith"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            {isPencilOutAdmin && (
              <option value="pencilout_admin">PencilOut staff (cross-org)</option>
            )}
            <option value="org_admin">Org admin (manage org users)</option>
            <option value="org_member">Org member (regular user)</option>
          </select>
        </label>

        {needsOrg && (
          <label className="block">
            <span className="label-mono">Organization</span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              <option value="">— select an organization —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {orgs.length === 0 && (
              <span className="mt-1 block text-xs text-burnt">
                No organizations yet. Create one first.
              </span>
            )}
          </label>
        )}
      </div>

      {invite.error && (
        <p className="text-sm text-red-pencil">
          {invite.error instanceof ApiError ? invite.error.detail : "Could not invite user."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            invite.isPending || !email.trim() || !displayName.trim() || (needsOrg && !orgId)
          }
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          {invite.isPending ? "Inviting…" : "Send invite"}
        </button>
      </div>
    </form>
  );
}

/** "Sign in as" button — pencilout_admin only. POSTs the
 *  impersonation endpoint, which swaps the auth cookie server-side;
 *  we just refetch the queries so /auth/me returns the new identity
 *  with ``impersonated_by`` set, and the layout banner appears.
 *
 *  Uses the in-app ConfirmDialog for the "are you sure?" step
 *  (replaces window.confirm — same brand styling as everywhere else).
 *  After confirm, navigates to ``/`` so the user lands on the
 *  target's dashboard rather than the admin's Users page. */
function ImpersonateButton({
  userId,
  displayName,
}: {
  userId: string;
  displayName: string;
}) {
  const impersonate = useImpersonateUser();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  function onConfirm() {
    impersonate.mutate(userId, {
      onSuccess: () => {
        setConfirming(false);
        // Drop the user on the dashboard so the layout (incl. the
        // pencil-yellow ImpersonationBanner) re-renders under the
        // new identity. The Users page would re-evaluate role and
        // try to render a list the target user can't see.
        navigate("/");
      },
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={impersonate.isPending}
        title={`Sign in as ${displayName}`}
        className="rounded p-1.5 text-graphite-soft transition hover:bg-pencil-yellow-soft hover:text-graphite disabled:opacity-50"
      >
        <LogIn className="h-3.5 w-3.5" aria-hidden />
      </button>

      {confirming && (
        <ConfirmDialog
          title={`Sign in as ${displayName}?`}
          body={
            <>
              You'll see exactly what they see. Their account state is unchanged. The pencil-yellow
              banner at the top of every page reverts you to your admin session.
              {impersonate.error && (
                <span className="mt-2 block text-red-pencil">
                  {impersonate.error instanceof ApiError
                    ? impersonate.error.detail
                    : "Could not sign in."}
                </span>
              )}
            </>
          }
          confirmLabel="Sign in as"
          busy={impersonate.isPending}
          onConfirm={onConfirm}
          onCancel={() => {
            setConfirming(false);
            impersonate.reset();
          }}
        />
      )}
    </>
  );
}

/** Edit-identity button + modal — pencilout_admin escape hatch.
 *
 *  Pre-launch fix for typo / address-change cases. Bypasses the
 *  verification dance on purpose (admin-only, audit-logged on the
 *  server). The self-service path with magic-link / OTP confirmation
 *  lives in the user's own profile page (POST /me/email/request-change).
 *
 *  At least one of email / phone_number must remain set after the
 *  edit; the server enforces this and 422s otherwise. */
function EditIdentityButton({ user }: { user: UserResponse }) {
  const updateUser = useUpdateUser(user.id);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user.email ?? "");
  const [phone, setPhone] = useState(user.phone_number ?? "");

  // Reset the form whenever the modal opens — picks up upstream edits
  // someone else made and avoids stale values from a prior aborted edit.
  function handleOpen() {
    setEmail(user.email ?? "");
    setPhone(user.phone_number ?? "");
    updateUser.reset();
    setOpen(true);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    // Guard client-side too so the server's 422 isn't the user's first
    // signal that they nuked both fields.
    if (!trimmedEmail && !trimmedPhone) return;
    const body: { email?: string; phone_number?: string } = {};
    // Only send fields that actually changed — saves a no-op write
    // and avoids resetting phone_verified when the phone hasn't
    // changed (the server clears phone_verified_at on every phone
    // update).
    if (trimmedEmail !== (user.email ?? "")) {
      body.email = trimmedEmail;
    }
    if (trimmedPhone !== (user.phone_number ?? "")) {
      body.phone_number = trimmedPhone;
    }
    if (Object.keys(body).length === 0) {
      setOpen(false);
      return;
    }
    try {
      await updateUser.mutateAsync(body);
      setOpen(false);
    } catch {
      /* error surfaces via updateUser.error below */
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title="Edit email / phone (admin)"
        className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/40 p-4"
          // biome-ignore lint/a11y/useSemanticElements: custom modal pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that this component doesn't use.
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md space-y-4 rounded-lg border border-rule bg-paper p-5 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite">
                Edit identity — {user.display_name}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-graphite-soft hover:text-graphite"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-graphite-soft">
              Admin override — bypasses the verification flow. Use this only for typos and
              out-of-band fixes. The user can update their own contact info via their profile page
              (with magic-link / OTP verification). Setting a new phone clears their{" "}
              <span className="font-mono">phone_verified</span> flag until they re-OTP.
            </p>

            <label className="block">
              <span className="label-mono">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="label-mono">Phone</span>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(704) 555-1234"
                className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
              <span className="mt-1 block text-[11px] text-graphite-faint">
                10-digit US or +&lt;country&gt;&lt;number&gt;.
              </span>
            </label>

            {updateUser.error && (
              <p className="text-xs text-red-pencil">
                {updateUser.error instanceof ApiError
                  ? updateUser.error.detail
                  : "Could not update user."}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateUser.isPending || (!email.trim() && !phone.trim())}
                className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
              >
                {updateUser.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
